//================================================================
//  Page: Digital Commercial Request Form 
//================================================================

//  Purpose: This is the Digital Commercial Request form for just Digital users

//================================================================

//Libraries
import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';

//Contexts
import { GetUser } from '../../Library/GlobalContexts';

//Components
import PageComponent from '../../Components/PageComponent/PageComponent';
import EstablishNewAgreement from './Forms/EstablishNewAgreement';
import VaryExtendRenewExistingAgreement from './Forms/VaryExtendRenewExistingAgreement';
import TerminateExistingAgreement from './Forms/TerminateExistingAgreement';
import AssignNovateAgreement from './Forms/AssignNovateAgreement';
import EstablishNDA from './Forms/EstablishNDA';

//Functions

//Images
import ArrowIcon from '../../Components/Images/Icon_FormArrow_Grey.svg';

//CSS

export default function DigitalCommercialRequestForm() {

  //------------------------------------------------------
  //  useContexts & React Router
  //------------------------------------------------------

  const getUser = useContext(GetUser);
  const navigate = useNavigate();

  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

  // Controller for which form is showing > 'New Agreement', 'Vary/Extend/Renew Agreement', 'Terminate Existing Agreement', 'Assign/Novate Agreement', 'Establish NDA'
  const [form, setForm] = useState('');

  // Contains data user submitted in the form
  const [formSubmission, setFormSubmission] = useState(undefined);

  // Used to save page status > 'pending', 'onload', 'error-invalid', 'error-fatal'
  const [pageStatus, setPageStatus] = useState('onload');

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  // Get the correct format for the tile
  const getFormat = (formTitle) => {

    // Default state - no form is showing
    if (form === '') return 'bg-white flex flex-col gap-2 rounded-[6px] w-[32%] h-32 p-6 overflow-hidden shadow-md border-solid border-1 border-neutral-200 cursor-pointer';

    // Selected state
    else if (form === formTitle) return 'bg-white flex flex-col gap-2 rounded-[6px] w-[32%] h-32 p-6 overflow-hidden shadow-md border-solid border-1 border-[#0972D3] cursor-pointer';

    // Disabled state
    else return 'bg-neutral-100 flex flex-col gap-2 rounded-[6px] w-[32%] h-32 p-6 overflow-hidden shadow-md border-solid border-1 border-neutral-200 cursor-pointer';

  }

  // Handler used to show the correct form depending on which tile is currently selected
  const showForm = () => {

    if (form === 'Establish New Agreement') return <EstablishNewAgreement setPageStatus={setPageStatus} setFormSubmission={setFormSubmission}></EstablishNewAgreement>;

    else if (form === 'Vary/Extend/Renew Existing Agreement') return <VaryExtendRenewExistingAgreement setPageStatus={setPageStatus} setFormSubmission={setFormSubmission}></VaryExtendRenewExistingAgreement>;

    else if (form === 'Terminate Existing Agreement') return <TerminateExistingAgreement setPageStatus={setPageStatus} setFormSubmission={setFormSubmission}></TerminateExistingAgreement>;

    else if (form === 'Assign/Novate Agreement') return <AssignNovateAgreement setPageStatus={setPageStatus} setFormSubmission={setFormSubmission}></AssignNovateAgreement>;

    else if (form === 'Establish NDA') return <EstablishNDA setPageStatus={setPageStatus} setFormSubmission={setFormSubmission}></EstablishNDA>;

  }

  //------------------------------------------------------
  //  useEffects
  //------------------------------------------------------


  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

    return (
        <PageComponent
            status={pageStatus}
            header={
                <div className='flex flex-col p-10 gap-1 w-full border-solid border-1 border-neutral-300'>
                    <h4 className='h-full cursor-default text-[22px]'>
                        Raise a Digital Commercial Request
                    </h4>
                    <p className='text-[#424242] font-sans text-[16px] leading-[24px] m-0'>
                        Use this form to request support from the one of the Lendlease Digital Commercial teams. Please provide as much relevant information as possible in the form below.
                    </p>
                </div>
            }
            body={
                <div className='flex flex-col p-4 gap-4 bg-neutral-100 w-full border-solid border-1 border-neutral-300'>
 
                    {/* Banner */}
                    <div className='bg-[#EDF0FA] p-[20px] border-l-[7px] border-l-[#4965CB]'>
                        <h5 className='text-[18px]'>
                            Funding Sources: Digital vs. Business Requests
                        </h5>

                        <p className='m-0 text-[#535353]'>
                            Initiatives directly funded by IDCF necessitate business LoAs, and must be submitted via the <b>Demand Management Process</b>.
                        </p>
                    </div>

                    {/* Select Form Container */}
                    <div className='relative bg-white rounded-[10px] px-8 py-6 w-full shadow-md border-solid border-1 border-neutral-300'>

                        <h6 className='font-semibold mb-2 my-1'>What type of request is this?</h6>

                        <div className='hidden md:flex flex-wrap gap-3 py-3 place-content-between'>

                        {/* Establish New Agreement */}
                        <div className={getFormat('Establish New Agreement')} onClick={() => setForm('Establish New Agreement')}>
                            <p className='font-semibold mb-0'>Establish New Agreement</p>
                            <p className='line-clamp-2 mb-0 text-[#424242]'>
                                Launch a new <u>Digital funded</u> initiative, trial or onboard a new technology.
                            </p>
                        </div>

                        {/* Vary/Extend/Renew Existing Agreement */}
                        <div className={getFormat('Vary/Extend/Renew Existing Agreement')} onClick={() => setForm('Vary/Extend/Renew Existing Agreement')}>
                            <p className='font-semibold mb-0'>Vary/Extend/Renew Existing Agreement</p>
                            <p className='line-clamp-2 mb-0 text-[#424242]'>
                                Renew or vary an existing <u>Digital funded</u> related contract.
                            </p>
                        </div>

                        {/* Terminate Existing Agreement */}
                        <div className={getFormat('Terminate Existing Agreement')} onClick={() => setForm('Terminate Existing Agreement')}>
                            <p className='font-semibold mb-0'>Terminate Existing Agreement</p>
                            <p className='line-clamp-2 mb-0 text-[#424242]'>
                                Retire a <u>Digital funded</u> agreement that is no longer needed.
                            </p>
                        </div>

                        {/* Assign/Novate Agreement */}
                        <div className={getFormat('Assign/Novate Agreement')} onClick={() => setForm('Assign/Novate Agreement')}>
                            <p className='font-semibold mb-0'>Assign/Novate Agreement</p>
                            <p className='line-clamp-2 mb-0 text-[#424242]'>
                                Transfer contractual rights, not obligations, to a third party.
                            </p>
                        </div>

                        {/* Establish NDA */}
                        <div className={getFormat('Establish NDA')} onClick={() => setForm('Establish NDA')}>
                            <p className='font-semibold mb-0'>Establish NDA</p>
                            <p className='line-clamp-2 mb-0 text-[#424242]'>
                                Raise an NDA for Digital initiative, trial or new technology.
                            </p>
                        </div>

                        {/* Placeholder */}
                        <div className=' w-[32%]'></div>

                        </div>

                        {/* Mobile - Show Dropdown */}
                        <div className='md:hidden'>
                        <select className='Input-Field-Select w-full' onChange={(e) => setForm(e.target.value)}>
                            <option hidden value=''>Select</option>
                            <option value='Establish New Agreement'>Establish New Agreement</option>
                            <option value='Vary/Extend/Renew Existing Agreement'>Vary/Extend/Renew Existing Agreement</option>
                            <option value='Terminate Existing Agreement'>Terminate Existing Agreement</option>
                            <option value='Assign/Novate Agreement'>Assign/Novate Agreement</option>
                            <option value='Establish NDA'>Establish NDA</option>
                        </select>
                        </div>

                        {/* Arrow (appears when option selected) */}
                        <div className='absolute z-50 -bottom-9 left-[42%] md:left-[45%] ' hidden={form === ''}>
                            <img src={ArrowIcon} alt='Arrow' className='w-[50px] border border-1 rounded-full'></img>
                        </div>

                    </div>

                    {/* =============================== */}
                    {/*          REQUEST FORM           */}
                    {/* =============================== */}

                    <div className='bg-white rounded-[10px] p-6 w-full shadow-md border-solid border-1 border-neutral-300' hidden={form === ''}>

                        {/* Gets selected form */}
                        {showForm()}

                    </div>

                </div>
            }
            successContent={
                <div className='flex flex-col p-6 gap-4 bg-neutral-100 w-full border-solid border-1 border-neutral-300'>

                    <div className='bg-[#e7f2da] p-4 flex flex-col gap-2 rounded-md shadow-md border-l-8 border-l-[#8ABB45]'>
                        <label className='font-semibold text-lg'>
                            {formSubmission?.requesttype} Request Submitted
                        </label>
                        <p className='text-neutral-600'>
                            <strong>{getUser?.givenname} {getUser?.surname} </strong> has submitted {['a', 'e', 'i', 'o', 'u'].includes.formSubmission?.requesttype[0] ? 'an' : 'a'} {formSubmission?.requesttype.toLowerCase()} request on {formSubmission?.createddate?.toLocaleString()}. View <Link to={{ pathname: `/requests/${formSubmission?.parentrequestid}` }}>{formSubmission?.parentrequestid?.toUpperCase()}</Link>.
                        </p>
                    </div>

                    <div className='flex gap-2 flex-col md:flex-row'>

                        <button 
                            className='Primary-Button' 
                            onClick={() => {
                                setForm('');
                                setPageStatus('onload');
                                navigate('/requests');
                        }}>
                            Go to My Requests
                        </button>

                        <button 
                            className='Secondary-Button' 
                            onClick={() => {
                                setForm('');
                                setPageStatus('onload');
                        }}>
                            Submit another request
                        </button>

                    </div>

                </div>
            }
        ></PageComponent>
    )
}